// RPGish route definitions
// Uses d3-kit's path-based Router (pushState)

import { Router } from '@dice-driven/d3-kit';

// Create singleton instance
export const router = new Router();

// Title layout — title screen background, modal routes overlay app-viewport
router.addRoute('/', 'title', {
  layout: 'title',
  auth: false,
  music: 'title',
});
router.addRoute('/play', 'play', {
  layout: 'title',
  auth: true,
  music: 'title',
  titleMenu: 'play',
});
router.addRoute('/play/adventures', 'play-adventures', {
  layout: 'title',
  auth: true,
  music: 'title',
  modal: { heading: 'Adventures', view: 'play-view', backHref: '/play', backLabel: 'Play' },
});
router.addRoute('/account', 'account', {
  layout: 'title',
  auth: true,
  music: 'title',
  modal: { heading: 'Account', view: 'account-view' },
});
router.addRoute('/notifications', 'notifications', {
  layout: 'title',
  auth: true,
  music: 'title',
  modal: { heading: 'Notifications', view: 'notifications-view' },
});
router.addRoute('/friends', 'friends', {
  layout: 'title',
  auth: true,
  music: 'title',
  modal: { heading: 'Friends', view: 'friends-view' },
});
router.addRoute('/games', 'games', {
  layout: 'title',
  auth: true,
  music: 'title',
  modal: { heading: 'My Games', view: 'games-view' },
});
router.addRoute('/adventures', 'adventures', {
  layout: 'title',
  auth: true,
  music: 'title',
  modal: { heading: 'Adventures', view: 'adventures-view' },
});
router.addRoute('/adventures/:slug/start', 'start-adventure', {
  layout: 'title',
  auth: true,
  music: 'title',
  modal: {
    heading: 'Start Adventure',
    view: 'start-adventure-view',
    attrs: { slug: 'slug' },
    backHref: '/play/adventures',
    backLabel: 'Adventures',
  },
});
router.addRoute('/characters', 'characters', {
  layout: 'title',
  auth: true,
  music: 'title',
  modal: { heading: 'My Characters', view: 'characters-view', backHref: '/play', backLabel: 'Play' },
});
router.addRoute('/characters/new', 'new-character', {
  layout: 'title',
  auth: true,
  music: 'title',
  modal: { heading: 'New Character', view: 'choose-character-mode-view', noPadding: true },
});
router.addRoute('/characters/new/quick', 'quick-create-character', {
  layout: 'title',
  auth: true,
  music: 'title',
  modal: { heading: 'Quick Create Character', view: 'quick-create-view', noPadding: true },
});
router.addRoute('/characters/new/custom', 'custom-create-character', {
  layout: 'title',
  auth: true,
  music: 'title',
  modal: { heading: 'Create Character', view: 'create-character-view', noPadding: true },
});
router.addRoute('/characters/:characterId', 'character-detail', {
  layout: 'title',
  auth: true,
  music: 'title',
  modal: {
    heading: 'Character',
    view: 'character-detail-view',
    attrs: { 'character-id': 'characterId' },
    backHref: '/characters',
    backLabel: 'Characters',
  },
});
router.addRoute('/create', 'create', {
  layout: 'title',
  auth: true,
  music: 'title',
  titleMenu: 'create',
});
router.addRoute('/campaign', 'campaign', {
  layout: 'title',
  auth: true,
  music: 'title',
  titleMenu: 'campaign',
});
router.addRoute('/survival', 'survival', {
  layout: 'title',
  auth: true,
  music: 'title',
  titleMenu: 'survival',
});
router.addRoute('/terms', 'terms', {
  layout: 'title',
  auth: false,
  music: 'title',
  modal: { heading: 'Terms of Service', view: 'terms-of-service' },
});
router.addRoute('/privacy', 'privacy', {
  layout: 'title',
  auth: false,
  music: 'title',
  modal: { heading: 'Privacy Policy', view: 'privacy-policy' },
});

// Fullscreen layout — top-bar + full-viewport
router.addRoute('/game/:gameId', 'game', {
  layout: 'fullscreen',
  auth: true,
  music: 'silent',
  view: 'game-view',
  attrs: { 'game-id': 'gameId' },
});
// Staff-only spectator view of a game. Server gates on operate:rpgish.
router.addRoute('/spectate/:gameId', 'spectate', {
  layout: 'fullscreen',
  auth: true,
  music: 'silent',
  view: 'game-spectator-view',
  attrs: { 'game-id': 'gameId' },
});
router.addRoute('/game/:gameId/join', 'join-game', {
  layout: 'fullscreen',
  auth: true,
  music: 'silent',
  view: 'join-game-view',
  attrs: { 'game-id': 'gameId' },
});
router.addRoute('/friends/accept/:code', 'friends-accept', {
  layout: 'fullscreen',
  auth: true,
  music: 'silent',
  view: 'friends-accept-view',
  attrs: { code: 'code' },
});

// Public layout — no auth, no top-bar
router.addRoute('/access-denied', 'access-denied', {
  layout: 'public',
  auth: false,
  music: 'title',
  view: 'access-denied-view',
});
router.addRoute('/playtest-notes', 'playtest-notes', {
  layout: 'public',
  auth: false,
  music: 'title',
  view: 'playtest-notes-view',
});

// Staff tabbed layout — top-bar + staff-view with tabs
router.addRoute('/staff/users', 'staff-users', { layout: 'staff', auth: true, music: 'silent', staffTab: 'users' });
router.addRoute('/staff/invitations', 'staff-invitations', {
  layout: 'staff',
  auth: true,
  music: 'silent',
  staffTab: 'invitations',
});
router.addRoute('/staff/games', 'staff-games', { layout: 'staff', auth: true, music: 'silent', staffTab: 'games' });
router.addRoute('/staff/adventures', 'staff-adventures', {
  layout: 'staff',
  auth: true,
  music: 'silent',
  staffTab: 'adventures',
});
router.addRoute('/staff/equipment', 'staff-equipment', {
  layout: 'staff',
  auth: true,
  music: 'silent',
  staffTab: 'equipment',
});
router.addRoute('/staff/classes', 'staff-classes', {
  layout: 'staff',
  auth: true,
  music: 'silent',
  staffTab: 'classes',
});
router.addRoute('/staff/feats', 'staff-feats', { layout: 'staff', auth: true, music: 'silent', staffTab: 'feats' });
router.addRoute('/staff/backgrounds', 'staff-backgrounds', {
  layout: 'staff',
  auth: true,
  music: 'silent',
  staffTab: 'backgrounds',
});
router.addRoute('/staff/species', 'staff-species', {
  layout: 'staff',
  auth: true,
  music: 'silent',
  staffTab: 'species',
});
router.addRoute('/staff/spells', 'staff-spells', { layout: 'staff', auth: true, music: 'silent', staffTab: 'spells' });
router.addRoute('/staff/weapon-masteries', 'staff-weapon-masteries', {
  layout: 'staff',
  auth: true,
  music: 'silent',
  staffTab: 'weapon-masteries',
});
router.addRoute('/staff/paperdoll', 'staff-paperdoll', {
  layout: 'staff',
  auth: true,
  music: 'silent',
  staffTab: 'paperdoll',
});
router.addRoute('/staff/monsters', 'staff-monsters', {
  layout: 'staff',
  auth: true,
  music: 'silent',
  staffTab: 'monsters',
});

// Staff editor layout — top-bar + full-page editor (no tabs)
router.addRoute('/staff/adventures/new', 'staff-adventures-new', {
  layout: 'staff-editor',
  auth: true,
  music: 'silent',
  view: 'staff-adventure-form-view',
});
router.addRoute('/staff/adventures/:slug/edit', 'staff-adventures-edit', {
  layout: 'staff-editor',
  auth: true,
  music: 'silent',
  view: 'staff-adventure-form-view',
  attrs: { slug: 'slug' },
});
router.addRoute('/staff/adventures/:slug/maps/:mapId/edit', 'staff-map-editor', {
  layout: 'fullscreen',
  auth: true,
  music: 'silent',
  view: 'staff-map-editor-view',
  attrs: { 'adventure-slug': 'slug', 'map-id': 'mapId' },
});
router.addRoute('/staff/equipment/new', 'staff-equipment-new', {
  layout: 'staff-editor',
  auth: true,
  music: 'silent',
  view: 'staff-equipment-form-view',
});
router.addRoute('/staff/equipment/:slug/edit', 'staff-equipment-edit', {
  layout: 'staff-editor',
  auth: true,
  music: 'silent',
  view: 'staff-equipment-form-view',
  attrs: { slug: 'slug' },
});
router.addRoute('/staff/classes/new', 'staff-classes-new', {
  layout: 'staff-editor',
  auth: true,
  music: 'silent',
  view: 'staff-class-editor-view',
});
router.addRoute('/staff/classes/:slug/edit', 'staff-classes-edit', {
  layout: 'staff-editor',
  auth: true,
  music: 'silent',
  view: 'staff-class-editor-view',
  attrs: { slug: 'slug' },
});
router.addRoute('/staff/backgrounds/new', 'staff-backgrounds-new', {
  layout: 'staff-editor',
  auth: true,
  music: 'silent',
  view: 'staff-background-editor-view',
});
router.addRoute('/staff/backgrounds/:slug/edit', 'staff-backgrounds-edit', {
  layout: 'staff-editor',
  auth: true,
  music: 'silent',
  view: 'staff-background-editor-view',
  attrs: { slug: 'slug' },
});
router.addRoute('/staff/feats/new', 'staff-feats-new', {
  layout: 'staff-editor',
  auth: true,
  music: 'silent',
  view: 'staff-feat-editor-view',
});
router.addRoute('/staff/feats/:slug/edit', 'staff-feats-edit', {
  layout: 'staff-editor',
  auth: true,
  music: 'silent',
  view: 'staff-feat-editor-view',
  attrs: { slug: 'slug' },
});
router.addRoute('/staff/species/new', 'staff-species-new', {
  layout: 'staff-editor',
  auth: true,
  music: 'silent',
  view: 'staff-species-editor-view',
});
router.addRoute('/staff/species/:slug/edit', 'staff-species-edit', {
  layout: 'staff-editor',
  auth: true,
  music: 'silent',
  view: 'staff-species-editor-view',
  attrs: { slug: 'slug' },
});
router.addRoute('/staff/spells/new', 'staff-spells-new', {
  layout: 'staff-editor',
  auth: true,
  music: 'silent',
  view: 'staff-spell-editor-view',
});
router.addRoute('/staff/spells/:slug/edit', 'staff-spells-edit', {
  layout: 'staff-editor',
  auth: true,
  music: 'silent',
  view: 'staff-spell-editor-view',
  attrs: { slug: 'slug' },
});
router.addRoute('/staff/weapon-masteries/new', 'staff-weapon-masteries-new', {
  layout: 'staff-editor',
  auth: true,
  music: 'silent',
  view: 'staff-weapon-mastery-editor-view',
});
router.addRoute('/staff/weapon-masteries/:slug/edit', 'staff-weapon-masteries-edit', {
  layout: 'staff-editor',
  auth: true,
  music: 'silent',
  view: 'staff-weapon-mastery-editor-view',
  attrs: { slug: 'slug' },
});
router.addRoute('/staff/monsters/new', 'staff-monsters-new', {
  layout: 'staff-editor',
  auth: true,
  music: 'silent',
  view: 'staff-monster-editor-view',
});
router.addRoute('/staff/monsters/:slug/edit', 'staff-monsters-edit', {
  layout: 'staff-editor',
  auth: true,
  music: 'silent',
  view: 'staff-monster-editor-view',
  attrs: { slug: 'slug' },
});
router.addRoute('/staff/games/:gameId/events', 'staff-game-events', {
  layout: 'staff-editor',
  auth: true,
  music: 'silent',
  view: 'staff-game-events-view',
  attrs: { 'game-id': 'gameId' },
});
